// ============================================================
// FILE: ExclusiveStatusCard.js
// LOCATION: C:\gardenmarket\web\src\components\ExclusiveStatusCard.js
// ============================================================
// PURPOSE:
//   Shows who a directed job went to, and gives the two people
//   allowed to end that exclusivity a way to do it.
//
// WHY THIS EXISTS:
//   Referral jobs are exclusive PERMANENTLY — exclusive_until is
//   NULL and no timer ever opens them. That is deliberate: a
//   gardener who might lose their own customer to a rival would
//   never share their booking link.
//
//   The counterweight is that the BUYER can release the job. It
//   is never automatic; it is a decision the buyer makes when a
//   gardener has not replied. Without that, a referral job whose
//   gardener is sick, on holiday or simply not looking sits
//   invisible forever, and the buyer never comes back.
//
//   The API endpoint and both procs shipped on 2026-08-24. This
//   component is the UI that was missing.
//
// TWO CALLERS, enforced server-side in usp_job_release_exclusive:
//   - the buyer          ("he hasn't replied")
//   - the targeted gardener (passing on it)
//
// RELEASE IS ONE-WAY. There is no un-release, so both paths
// confirm before acting.
//
// PROPS:
//   jobId     - job to inspect
//   role      - 'buyer' | 'gardener'
//   onChanged - called after a successful release so the parent
//               can reload
// ============================================================

import { useState, useEffect } from 'react';
import client from '../api/client';

export default function ExclusiveStatusCard({ jobId, role, onChanged }) {
    const [status,     setStatus]     = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [releasing,  setReleasing]  = useState(false);
    const [error,      setError]      = useState('');

    useEffect(() => {
        let cancelled = false;
        client.get(`/jobs/${jobId}/exclusive-status`)
            .then(r => { if (!cancelled) setStatus(r.data.status || r.data); })
            .catch(() => { /* non-fatal: the card simply does not render */ });
        return () => { cancelled = true; };
    }, [jobId]);

    if (!status) return null;

    // Not a directed job at all — nothing to say.
    if (!status.exclusive_to_gardener_id) return null;

    const name        = (status.gardener_name || '').split(' ')[0] || 'this gardener';
    const isReleased  = !!status.released_at;
    const isPermanent = status.is_permanent === 1 || status.is_permanent === true;
    const hasQuoted   = (status.gardener_has_quoted || 0) > 0;
    const hoursLeft   = status.hours_left;

    const handleRelease = async () => {
        setReleasing(true);
        setError('');
        try {
            await client.post(`/jobs/${jobId}/release`, {
                reason: role === 'buyer' ? 'buyer_request' : 'gardener_declined',
            });
            const r = await client.get(`/jobs/${jobId}/exclusive-status`);
            setStatus(r.data.status || r.data);
            setConfirming(false);
            if (onChanged) onChanged();
        } catch (err) {
            setError(err.response?.data?.error || 'Could not open the job. Please try again.');
        } finally {
            setReleasing(false);
        }
    };

    // ---------- Already open ----------
    // Quiet, factual. The buyer decided this; do not celebrate it
    // or imply the gardener did something wrong.
    if (isReleased) {
        return (
            <div style={S.wrapNeutral}>
                <div style={S.textNeutral}>
                    {role === 'buyer'
                        ? `This job is open to all local gardeners. ${name} can still quote.`
                        : 'This job is now open to all local gardeners.'}
                </div>
            </div>
        );
    }

    // ---------- Gardener's view ----------
    if (role === 'gardener') {
        return (
            <div style={S.wrap}>
                <div style={S.heading}>
                    {isPermanent ? 'Came through your booking link' : 'You have first look'}
                </div>
                <div style={S.text}>
                    {isPermanent
                        ? 'This customer used your link, so this job is yours alone — no other gardener can see it.'
                        : `You have first look${hoursLeft > 0 ? ` for another ${hoursLeft} hour${hoursLeft === 1 ? '' : 's'}` : ''}. After that other local gardeners can quote too.`}
                </div>

                {!confirming && (
                    <button type="button" style={S.linkBtn} onClick={() => setConfirming(true)}>
                        Can't take this one? Pass it to other gardeners
                    </button>
                )}

                {confirming && (
                    <div style={S.confirmBox}>
                        <div style={S.confirmText}>
                            This opens the job to other local gardeners straight away, so
                            the customer is not left waiting. You can still quote if you
                            change your mind. This cannot be undone.
                        </div>
                        <div style={S.confirmRow}>
                            <button type="button" style={S.dangerBtn} disabled={releasing} onClick={handleRelease}>
                                {releasing ? 'Opening…' : 'Yes, pass it on'}
                            </button>
                            <button type="button" style={S.cancelBtn} disabled={releasing} onClick={() => setConfirming(false)}>
                                Keep it
                            </button>
                        </div>
                    </div>
                )}

                {error && <div style={S.error}>{error}</div>}
            </div>
        );
    }

    // ---------- Buyer's view ----------
    return (
        <div style={S.wrap}>
            <div style={S.heading}>
                {isPermanent ? `Sent to ${name} only` : `${name} has first look`}
            </div>
            <div style={S.text}>
                {isPermanent
                    ? (hasQuoted
                        ? `${name} has sent you a quote. No other gardeners can see this job.`
                        : `You came through ${name}'s booking link, so only ${name} can see this job.`)
                    : `${name} has first look${hoursLeft > 0 ? ` for another ${hoursLeft} hour${hoursLeft === 1 ? '' : 's'}` : ''}. After that other local gardeners can quote too.`}
            </div>

            {!confirming && (
                <button type="button" style={S.linkBtn} onClick={() => setConfirming(true)}>
                    {hasQuoted
                        ? 'Want more quotes to compare?'
                        : `Haven't heard back? Open this job to other gardeners`}
                </button>
            )}

            {confirming && (
                <div style={S.confirmBox}>
                    <div style={S.confirmText}>
                        Other local gardeners will be able to see this job and send you
                        quotes. {name} can still quote
                        {hasQuoted ? ", and the quote you already have stays" : ''}.
                        This cannot be undone.
                    </div>
                    <div style={S.confirmRow}>
                        <button type="button" style={S.dangerBtn} disabled={releasing} onClick={handleRelease}>
                            {releasing ? 'Opening…' : 'Yes, open to all gardeners'}
                        </button>
                        <button type="button" style={S.cancelBtn} disabled={releasing} onClick={() => setConfirming(false)}>
                            Keep waiting
                        </button>
                    </div>
                </div>
            )}

            {error && <div style={S.error}>{error}</div>}
        </div>
    );
}

// Colours match the directed-job banner in PostJob.js so the
// buyer sees the same visual language from posting through to
// quoting.
const S = {
    wrap: {
        background: '#e8f5e9', border: '1px solid #a5d6a7',
        borderRadius: '8px', padding: '14px 16px', marginBottom: '16px',
    },
    wrapNeutral: {
        background: '#f5f5f5', border: '1px solid #e0e0e0',
        borderRadius: '8px', padding: '12px 16px', marginBottom: '16px',
    },
    heading:     { fontWeight: 600, color: '#14532D', marginBottom: '4px' },
    text:        { fontSize: '0.85rem', color: '#14532D', lineHeight: 1.5 },
    textNeutral: { fontSize: '0.85rem', color: '#555', lineHeight: 1.5 },
    linkBtn: {
        background: 'none', border: 'none', padding: '8px 0 0',
        color: '#2d6a4f', fontSize: '0.8rem', cursor: 'pointer',
        textDecoration: 'underline', display: 'block', textAlign: 'left',
    },
    confirmBox: {
        marginTop: '10px', paddingTop: '10px',
        borderTop: '1px solid #a5d6a7',
    },
    confirmText: { fontSize: '0.82rem', color: '#14532D', lineHeight: 1.5, marginBottom: '10px' },
    confirmRow:  { display: 'flex', gap: '8px', flexWrap: 'wrap' },
    dangerBtn: {
        background: '#2d6a4f', color: '#fff', border: 'none',
        borderRadius: '6px', padding: '9px 14px',
        fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600,
    },
    cancelBtn: {
        background: '#fff', color: '#2d6a4f', border: '1px solid #a5d6a7',
        borderRadius: '6px', padding: '9px 14px',
        fontSize: '0.85rem', cursor: 'pointer',
    },
    error: { fontSize: '0.8rem', color: '#c0392b', marginTop: '8px' },
};
